import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { useHistory } from 'react-router';

import { config } from 'src/config';
import { defaultLocalStorageAdapter } from 'src/shared/graphql';
import { request } from 'src/shared/helpers/request';
import { loginPathWithRedirectTo } from 'src/shared/helpers/routing';
import { useLocation } from 'src/shared/hooks';
import { Route } from 'src/shared/route';
import { logError } from 'src/shared/telemetry/logger';

export interface UseLogoutProps {
  /**
   * Whether to include the `redirect_to` query parameter to redirect the user
   * back to where they were before logout upon logging back in
   *
   * @defaultValue `true`
   */
  redirectTo?: boolean;
}

export const useLogout = (
  { redirectTo = true }: UseLogoutProps = { redirectTo: true },
) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const apolloClient = useApolloClient();

  return useCallback(() => {
    try {
      void apolloClient.resetStore();

      void request(`${config.API_URI}/sessions`, {
        method: 'DELETE',
        credentials: 'include',
      });

      void defaultLocalStorageAdapter.removeAccessToken();
      void defaultLocalStorageAdapter.removeRefreshToken();
    } catch (cause) {
      const error = Error('An error occurred attempting to logout via API');
      error.cause = cause;
      logError(error);
    }

    history.push(
      redirectTo ? loginPathWithRedirectTo(pathname) : Route.LOGIN.path,
    );
  }, [apolloClient, history, pathname, redirectTo]);
};
